/* eslint-disable @typescript-eslint/ban-ts-comment */

/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
import {PlusIcon} from './icons';
import {NoteItem} from './note-item';
import {SectionHeader} from './section-header';
import {ReferencesTable} from './ui-components';
import Text from '@/components/react-native/text';
import TouchableOpacity from '@/components/react-native/touchable-opacity';
import View from '@/components/react-native/view';
import useAirportNote from '@/stores/airport-note';
import {
  ENoteCategory,
  ENoteTag,
  ENoteType,
  INoteInfo,
  IReference
} from '@/types/airport';
import classNames from 'classnames';
import React, {useMemo} from 'react';

export const AirportSectionHeader = React.memo(
  ({
    title,
    isExpanded,
    onToggle,
    backgroundColor,
    textColor,
    onAddNote
  }: {
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    backgroundColor?: string;
    textColor?: string;
    onAddNote?: () => void;
  }) => (
    <SectionHeader
      title={title}
      isExpanded={isExpanded}
      onToggle={onToggle}
      backgroundColor={backgroundColor}
      textColor={textColor}
      showAddButton={!!onAddNote}
      onAdd={onAddNote}
    />
  )
);

AirportSectionHeader.displayName = 'AirportSectionHeader';

const sortNotes = (notes: INoteInfo[]) => {
  // created_by Admin > approved_by Admin > created_by User > approved_by User
  // Then by updated_at latest to oldest, then created_at latest to oldest

  return notes.sort((a, b) => {
    // First, prioritize Admin notes
    if (a.created_by === 'Admin' && b.created_by !== 'Admin') {
      return -1;
    }
    if (b.created_by === 'Admin' && a.created_by !== 'Admin') {
      return 1;
    }

    // If both are Admin or both are not Admin, sort by date
    // First, compare updated_at if both exist
    if (a.updated_at && b.updated_at) {
      const updatedAtComparison =
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      if (updatedAtComparison !== 0) {
        return updatedAtComparison;
      }
    } else if (a.updated_at && !b.updated_at) {
      return -1; // a has updated_at, b doesn't - a comes first
    } else if (!a.updated_at && b.updated_at) {
      return 1; // b has updated_at, a doesn't - b comes first
    }

    // If updated_at is equal or both don't have it, compare created_at
    const createdAtA = new Date(a.created_at).getTime();
    const createdAtB = new Date(b.created_at).getTime();
    return createdAtB - createdAtA; // latest first
  });
};

export const AirportSectionContent = React.memo(
  ({
    category,
    isExpanded,
    airportNotes,
    onNotePress,
    onEditNote,
    backgroundColor
  }: {
    category: ENoteCategory;
    isExpanded: boolean;
    airportNotes: INoteInfo[];
    onNotePress: (note: INoteInfo) => void;
    onEditNote: (note: INoteInfo) => void;
    onFleetUpdate?: (
      category: ENoteCategory,
      fleetType: string,
      references: IReference[]
    ) => void;
    fleetTypes?: string[];
    backgroundColor?: string;
  }) => {
    // All notes for this category - consolidated instead of separated by type
    const allNotes = useMemo(
      () =>
        sortNotes(airportNotes.filter((note) => note.category === category)),
      [airportNotes, category]
    );

    return (
      <View
        className={classNames({
          hidden: !isExpanded
        })}
      >
        <View className="py-[8px]" style={{backgroundColor}}>
          {allNotes.map((note) => (
            <View key={note.id} data-note-id={note.id}>
              <NoteItem
                category={note.category}
                tags={note.tags}
                content={note.content}
                author={note.created_by}
                publishedAt={note.created_at}
                commentCount={note.comment_count}
                isNew={note.is_new}
                isPending={!note?.approved_by}
                showEdit={!!note?.approved_by}
                onPress={() => onNotePress(note)}
                onEdit={() => onEditNote(note)}
              />
            </View>
          ))}

          {allNotes.length === 0 && (
            <View className="px-[16px] py-[16px]">
              <Text
                fontFamily="Geist"
                className="text-[#78716C] text-[16px] font-[400] tracking-[-0.32px]"
              >
                No notes available.
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  }
);
AirportSectionContent.displayName = 'AirportSectionContent';

export const AirportSection = React.memo(
  ({
    title,
    category,
    isExpanded,
    onToggle,
    airportNotes,
    onNotePress,
    onAddNote
  }: {
    title: string;
    category: ENoteCategory;
    isExpanded: boolean;
    onToggle: () => void;
    airportNotes: INoteInfo[];
    onNotePress: (note: INoteInfo) => void;
    onAddNote: (category: ENoteCategory, type: ENoteType) => void;
  }) => {
    const airportInfo = useAirportNote((state) => state.airportNoteInfo);
    const references = useMemo(() => {
      if (!airportInfo) return [];
      // @ts-expect-error
      return airportInfo.categories[category].references ?? [];
    }, [airportInfo, category]);
    const notes = useMemo(
      () => airportNotes.filter((note) => note.category === category),
      [airportNotes, category]
    );

    return (
      <View>
        <SectionHeader
          title={title}
          isExpanded={isExpanded}
          onToggle={onToggle}
        />

        <View
          className={classNames({
            hidden: !isExpanded
          })}
        >
          <View className="py-[8px]">
            {/* References */}
            <View className="px-[16px] py-[18px]">
              <Text
                fontFamily="GeistMono"
                className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] uppercase mb-[16px]"
              >
                REFERENCES
              </Text>
              <ReferencesTable references={references} editable={false} />
            </View>

            {/* Procedural Notes */}
            <View className="flex-row items-center justify-between px-[16px]">
              <Text
                fontFamily="GeistMono"
                className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] uppercase"
              >
                PROCEDURAL NOTES
              </Text>
              <TouchableOpacity
                className="flex-row items-center gap-[6px] px-[16px] py-[8px]"
                onClick={() => onAddNote(category, ENoteType.PROCEDURAL_NOTES)}
              >
                <PlusIcon />
                <Text
                  fontFamily="Geist"
                  className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px]"
                >
                  Add Note
                </Text>
              </TouchableOpacity>
            </View>

            {notes.map((note) => (
              <NoteItem
                key={note.id}
                tags={note.tags}
                category={note.category}
                content={note.content}
                author={note.created_by}
                publishedAt={new Date(note.created_at).toLocaleString()}
                commentCount={note.comment_count}
                isNew={note.is_new}
                isPending={!note?.approved_by}
                showEdit={false}
                onPress={() => onNotePress(note)}
              />
            ))}

            {notes.length === 0 && (
              <View className="px-[16px] py-[16px]">
                <Text
                  fontFamily="Geist"
                  className="text-[#78716C] text-[16px] font-[400] tracking-[-0.32px]"
                >
                  No procedural notes available.
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  }
);
AirportSection.displayName = 'AirportSection';
